import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconMail01 } from "@pentabd/icons";
import { InputText, RadioGroup, Switch, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";

import { useGeoData } from "@hooks/useGeoData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import { getConvertedGeoData } from "@helpers/geoDataConvert";
import { getConvertedMasterData } from "@helpers/masterDataConvert";

import { GeneralInfoFormDataType } from "@validations/HRM/employee/generalInfoValidation";

import { FORM_FIELDS } from "@constants/form";
import { GEO_DATA } from "@constants/geo-data";
import { MASTER_DATA } from "@constants/master-data";

const { GENDER, RELIGION, MARITAL_STATUS, BLOOD_GROUPS } = MASTER_DATA;
const { DISTRICTS } = GEO_DATA;
const GENERAL_INFO = FORM_FIELDS.EMPLOYEE.GENERAL_INFO;

export const BasicInfo = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Those data are for some dropdowns or radio buttons options.
  const { data: gender } = useMasterData(GENDER);
  const { data: religion } = useMasterData(RELIGION);
  const { data: maritalStatus } = useMasterData(MARITAL_STATUS);
  const { data: bloodGroup } = useMasterData(BLOOD_GROUPS);
  const { data: districts } = useGeoData(DISTRICTS);

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<GeneralInfoFormDataType>();

  return (
    <div className="d-flex flex-column gap-8 mb-8 pb-8 border-bottom">
      <div className="row">
        <div className="col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("EMPLOYEE.GENERAL_INFO.FORM.NAME.LABEL")}
          </Text>
          <span className="text-danger">&#42;</span>
        </div>
        <div className="col-lg-7">
          <div className="row gy-0">
            <div className="col-6">
              <InputText
                {...register(GENERAL_INFO.NAME_IN_ENGLISH)}
                placeholder={t(
                  "EMPLOYEE.GENERAL_INFO.FORM.NAME.PLACEHOLDER_EN",
                )}
                className="fs-md"
                error={errors as any}
              />
              {/* <Text color="warning" size="xs">
                {t("EMPLOYEE.GENERAL_INFO.FORM.NAME.RESTRICTION")}
              </Text> */}
            </div>
            <div className="col-6">
              <InputText
                {...register(GENERAL_INFO.NAME_IN_BANGLA)}
                placeholder={t(
                  "EMPLOYEE.GENERAL_INFO.FORM.NAME.PLACEHOLDER_BN",
                )}
                className="fs-md"
                error={errors as any}
              />
              {/* <Text color="warning" size="xs">
                {t("EMPLOYEE.GENERAL_INFO.FORM.NAME.RESTRICTION")}
              </Text> */}
            </div>
          </div>
        </div>
      </div>

      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.NID_NO"
        register={{ ...register(GENERAL_INFO.NID_NO) }}
        error={errors as any}
        required
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.MOBILE_NO"
        register={{ ...register(GENERAL_INFO.MOBILE_NO) }}
        error={errors as any}
        required
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.EMAIL"
        register={{ ...register(GENERAL_INFO.EMAIL) }}
        error={errors as any}
        required
        restriction
        prefix={<IconMail01 customFill="#667085" size="20" />}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DATE_OF_BIRTH"
        control={control}
        errors={errors as any}
        name={GENERAL_INFO.DATE_OF_BIRTH}
        required
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DATE_OF_DEATH"
        control={control}
        errors={errors as any}
        name={GENERAL_INFO.DATE_OF_DEATH}
      />

      <div className="row">
        <div className="col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("EMPLOYEE.GENERAL_INFO.FORM.GENDER.LABEL")}
          </Text>
          <span className="text-danger">&#42;</span>
        </div>
        <div className="col-lg-7 d-flex flex-column gap-4">
          <Controller
            control={control}
            name={GENERAL_INFO.GENDER}
            render={({ field }) => {
              return (
                <div className="d-flex gap-8 flex-wrap">
                  <RadioGroup
                    {...register(GENERAL_INFO.GENDER)}
                    name={GENERAL_INFO.GENDER}
                    id="gender"
                    options={
                      getConvertedMasterData(gender, language, true) as any
                    }
                    error={errors as any}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>

      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.RELIGION"
        control={control}
        name={GENERAL_INFO.RELIGION}
        register={{ ...register(GENERAL_INFO.RELIGION) }}
        options={getConvertedMasterData(religion, language)}
        setValue={setValue}
        error={errors}
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.HOME_DISTRICT"
        control={control}
        name={GENERAL_INFO.HOME_DISTRICT}
        register={{ ...register(GENERAL_INFO.HOME_DISTRICT) }}
        options={getConvertedGeoData(districts, language)}
        setValue={setValue}
        error={errors}
        isSearchable
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.MARITAL_STATUS"
        control={control}
        name={GENERAL_INFO.MARITAL_STATUS}
        register={{ ...register(GENERAL_INFO.MARITAL_STATUS) }}
        options={getConvertedMasterData(maritalStatus, language)}
        setValue={setValue}
        error={errors}
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.BLOOD_GROUP"
        control={control}
        name={GENERAL_INFO.BLOOD_GROUP}
        register={{ ...register(GENERAL_INFO.BLOOD_GROUP) }}
        options={getConvertedMasterData(bloodGroup, language)}
        setValue={setValue}
        error={errors}
        required
      />
      <div className="row">
        <div className="col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("EMPLOYEE.GENERAL_INFO.FORM.FREEDOM_FIGHTER.LABEL")}
          </Text>
        </div>
        <div className="col-lg-7 d-flex flex-column gap-4">
          <Controller
            control={control}
            name={GENERAL_INFO.FREEDOM_FIGHTER}
            render={({ field }) => {
              return (
                <Switch
                  {...register(GENERAL_INFO.FREEDOM_FIGHTER)}
                  id={GENERAL_INFO.FREEDOM_FIGHTER}
                  name={GENERAL_INFO.FREEDOM_FIGHTER}
                  size="sm"
                  onChange={(e) =>
                    setValue(GENERAL_INFO.FREEDOM_FIGHTER, e.target.checked)
                  }
                  controlling
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
